import axiosInstance from '../helpers/axios'

/**
 * Phase 2 search overhaul — the consumer app queries the resactly-service `/search` proxy
 * (REACT_APP_SERVICE_URL) which forwards to resactly-dis. The browser no longer talks to
 * Elasticsearch directly for the results page.
 */
export interface SearchHit {
  type: 'restaurant' | 'dish' | 'foodArea' | string
  id: string
  score: number
  source: Record<string, unknown>
}

export interface SearchPage {
  items: SearchHit[]
  total: number
  page: number
  size: number
}

export interface GlobalSearchParams {
  q: string
  lat?: number
  lng?: number
  type?: string
  page?: number
  size?: number
}

export const fetchGlobalSearch = async (
  params: GlobalSearchParams,
): Promise<SearchPage> => {
  const { q, lat, lng, type, page = 0, size = 20 } = params
  const response = await axiosInstance.get<SearchPage>('/search', {
    params: { q, lat, lng, type, page, size },
  })
  return response.data
}

/**
 * Typeahead option shaped to match what GlobalSearch already consumes (the old @elastic/search-ui
 * AutocompleteResult had `.raw`-wrapped fields and a `_meta.rawHit._source`). This lets the backend
 * autocomplete drop in without rewriting GlobalSearch's render/select logic.
 */
export type AutocompleteType =
  | 'restaurant'
  | 'dish'
  | 'foodArea'
  | 'cuisine'
  | 'city'
  | string

export interface AutocompleteOption {
  // The entity type from the backend, used to render grouped-by-type sections (MUI `groupBy`).
  type?: AutocompleteType
  id: { raw: string }
  name?: { raw: string }
  city?: { raw: string }
  address1?: { raw: string }
  state?: { raw: string }
  key?: { raw: string }
  value?: string
  // Synthetic city rows carry a display label so the existing city click-routing keeps working.
  cityName?: string
  restaurantId?: string
  dishID?: string
  foodAreaId?: string
  _meta: { rawHit: { _source: Record<string, unknown>; _score: number } }
}

const toAutocompleteOption = (hit: SearchHit): AutocompleteOption => {
  const s = hit.source
  const str = (v: unknown): string | undefined =>
    v === undefined || v === null ? undefined : String(v)
  const id =
    str(s.restaurantId) ??
    str(s.dishID) ??
    str(s.foodAreaId) ??
    str(s.key) ??
    hit.id
  return {
    type: hit.type,
    id: { raw: id },
    name: s.name != null ? { raw: String(s.name) } : undefined,
    city: s.city != null ? { raw: String(s.city) } : undefined,
    address1: s.address1 != null ? { raw: String(s.address1) } : undefined,
    state: s.state != null ? { raw: String(s.state) } : undefined,
    key: s.key != null ? { raw: String(s.key) } : undefined,
    value: str(s.value),
    // City suggestions are derived from live restaurant data server-side; expose a display label so
    // GlobalSearch's existing `cityName && value` routing (?type=city&q=value) still fires.
    cityName: hit.type === 'city' ? str(s.city) ?? str(s.name) : undefined,
    restaurantId: str(s.restaurantId),
    dishID: str(s.dishID),
    foodAreaId: str(s.foodAreaId),
    _meta: { rawHit: { _source: s, _score: hit.score } },
  }
}

export const fetchGlobalAutocomplete = async (params: {
  q: string
  lat?: number
  lng?: number
  size?: number
}): Promise<AutocompleteOption[]> => {
  // `size` is the per-type quota server-side; keep it modest so each group gets a few guaranteed
  // slots without flooding the dropdown.
  const { q, lat, lng, size = 5 } = params
  const response = await axiosInstance.get<SearchPage>('/search/autocomplete', {
    params: { q, lat, lng, size },
  })
  return (response.data.items ?? []).map(toAutocompleteOption)
}

const asString = (v: unknown): string | undefined =>
  v === undefined || v === null ? undefined : String(v)

const asRaw = (v: unknown): { raw: string } => ({
  raw: v === undefined || v === null ? '' : String(v),
})

/**
 * Eater lookup for the social/group member picker (Phase 2 search overhaul). Replaces the browser's
 * direct-to-Elastic query on the eaters index — the app now calls the resactly-service `/search`
 * proxy, so the exposed Elastic key is gone and dis captures the search terms server-side. The
 * flattened result matches the fields the member picker renders/selects.
 */
export interface EaterSearchResult {
  eaterId: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  photo?: string
  instagram?: string
  facebook?: string
  tiktok?: string
  x?: string
}

export const fetchEaterSearch = async (params: {
  q: string
  size?: number
}): Promise<EaterSearchResult[]> => {
  const { q, size = 10 } = params
  const response = await axiosInstance.get<SearchPage>('/search/eaters', {
    params: { q, size },
  })
  return (response.data.items ?? []).map((hit) => {
    const s = hit.source
    return {
      eaterId: asString(s.eaterId) ?? hit.id,
      firstName: asString(s.firstName),
      lastName: asString(s.lastName),
      email: asString(s.email),
      phone: asString(s.phone),
      photo: asString(s.photo),
      instagram: asString(s.instagram),
      facebook: asString(s.facebook),
      tiktok: asString(s.tiktok),
      x: asString(s.x),
    }
  })
}

/**
 * Restaurant-owner dashboard typeahead (Phase 2). Reuses the unified `/search` proxy restricted to
 * the restaurant index; the `.raw`-wrapped shape lets the dashboard's result renderer stay as-is
 * after dropping the direct-to-Elastic connector.
 */
export interface RestaurantAutocompleteOption {
  name: { raw: string }
  address1: { raw: string }
  city: { raw: string }
  zipCode: { raw: string }
  placeId: { raw: string }
}

export const fetchRestaurantAutocomplete = async (params: {
  q: string
  size?: number
}): Promise<RestaurantAutocompleteOption[]> => {
  const { q, size = 8 } = params
  const response = await axiosInstance.get<SearchPage>('/search', {
    params: { q, type: 'restaurant', size },
  })
  return (response.data.items ?? []).map((hit) => {
    const s = hit.source
    return {
      name: asRaw(s.name),
      address1: asRaw(s.address1),
      city: asRaw(s.city),
      zipCode: asRaw(s.zipCode),
      placeId: asRaw(s.placeId),
    }
  })
}

/**
 * Onboarding/filter option typeahead (Phase 2). Replaces the per-index Elastic connector. The
 * caller sends a stable `category` token (mapped to the configured index server-side) rather than a
 * raw Elastic index name. Returns the distinct display strings the option list renders.
 */
export const fetchOptionSearch = async (params: {
  category: string
  field?: string
  q: string
  size?: number
}): Promise<string[]> => {
  const { category, field = 'value', q, size = 10 } = params
  const response = await axiosInstance.get<string[]>('/search/options', {
    params: { category, field, q, size },
  })
  return response.data ?? []
}

/**
 * FIPS lookup for the restaurant-owner region search (Phase 2). Replaces the browser's
 * direct-to-Elastic query on the fips index. Returns the resolved code, or null when none matches.
 */
export const fetchFips = async (params: {
  county: string
  state: string
}): Promise<string | null> => {
  const { county, state } = params
  const response = await axiosInstance.get<{ fips: string | null }>(
    '/search/fips',
    { params: { county, state } },
  )
  return response.data?.fips ?? null
}
